/**
 * Publish Error Classification & Recovery Hints
 *
 * Maps raw platform API error strings / HTTP codes to structured,
 * actionable messages shown in MultiAccountPublishModal.
 */

export interface PublishErrorInfo {
  title: string;
  hint: string;
  /** Label for the primary recovery action button (if any) */
  action?: string;
  /** Route to navigate to for the recovery action */
  actionRoute?: string;
}

/** Patterns matched against the raw error string (case-insensitive) */
const ERROR_PATTERNS: Array<{ pattern: RegExp; info: PublishErrorInfo }> = [
  // ── Auth / token ──────────────────────────────────────────────────────────
  {
    pattern: /token.*expir|session.*expir|auth.*expir|oauth.*expir/i,
    info: {
      title: 'Access token expired',
      hint: 'Your platform connection has expired. Re-connect your account to refresh the token.',
      action: 'Reconnect account',
      actionRoute: '/settings/businesses/active/connections',
    },
  },
  {
    pattern: /invalid.*token|token.*invalid|unauthorized|401/i,
    info: {
      title: 'Authorization failed',
      hint: 'The platform rejected your credentials. Try disconnecting and reconnecting your account.',
      action: 'Reconnect account',
      actionRoute: '/settings/businesses/active/connections',
    },
  },
  {
    pattern: /permission|scope|access denied|403/i,
    info: {
      title: 'Insufficient permissions',
      hint: 'Your account does not have Ads Manager permissions. Ensure you have the correct role on the ad account.',
      action: 'Check permissions',
      actionRoute: '/settings/businesses/active/connections',
    },
  },

  // ── Billing / budget ──────────────────────────────────────────────────────
  {
    pattern: /billing|payment|credit card|payment.*method|account.*suspended/i,
    info: {
      title: 'Billing issue on ad account',
      hint: 'The ad account has a billing problem. Check your payment method in the platform dashboard.',
    },
  },
  {
    pattern: /budget.*too.*low|minimum.*budget|daily.*budget/i,
    info: {
      title: 'Budget below platform minimum',
      hint: 'The daily or lifetime budget is below the platform\'s minimum. Increase the budget and regenerate the campaign.',
    },
  },

  // ── Meta campaign structure ───────────────────────────────────────────────
  {
    pattern: /is_adset_budget_sharing_enabled/i,
    info: {
      title: 'Missing budget sharing field',
      hint: 'Meta requires specifying whether ad sets share the campaign budget. This is a known issue that has been fixed — please try publishing again.',
    },
  },
  {
    pattern: /OAuthException.*code.*100|Invalid parameter.*OAuthException/i,
    info: {
      title: 'Invalid campaign parameter',
      hint: 'Meta rejected one of the campaign parameters. Check that all required fields are set correctly and try again.',
    },
  },

  // ── Content policy ────────────────────────────────────────────────────────
  {
    pattern: /policy.*violat|content.*reject|disapproved|ad.*review/i,
    info: {
      title: 'Ad content policy violation',
      hint: 'The ad creative was rejected for policy reasons. Review the ad copy and images, then ask the AI to regenerate.',
    },
  },
  {
    pattern: /image.*size|video.*size|media.*size|file.*too.*large/i,
    info: {
      title: 'Media file too large',
      hint: 'The image or video exceeds the platform\'s size limit. Re-crop or compress the media and retry.',
    },
  },
  {
    pattern: /image.*ratio|aspect.*ratio|dimensions/i,
    info: {
      title: 'Incorrect media aspect ratio',
      hint: 'The image dimensions don\'t match the required ratio. Use the crop tool to adjust before publishing.',
    },
  },

  // ── Rate limits ───────────────────────────────────────────────────────────
  {
    pattern: /rate.*limit|too.*many.*request|throttl|429/i,
    info: {
      title: 'Platform rate limit reached',
      hint: 'Too many requests were made to the platform API. Wait a few minutes and try again.',
    },
  },

  // ── Ad account ────────────────────────────────────────────────────────────
  {
    pattern: /ad.*account.*not.*found|account.*id.*invalid|no.*ad.*account/i,
    info: {
      title: 'Ad account not found',
      hint: 'The connected account doesn\'t have an active Ad Account. Verify account selection in Settings.',
      action: 'Check connections',
      actionRoute: '/settings/businesses/active/connections',
    },
  },
  {
    pattern: /page.*not.*found|page.*id|facebook.*page/i,
    info: {
      title: 'Facebook Page not linked',
      hint: 'No Facebook Page is linked to this ad account. Link a Page in Meta Business Suite first.',
    },
  },

  // ── Campaign structure ────────────────────────────────────────────────────
  {
    pattern: /campaign.*limit|too.*many.*campaigns/i,
    info: {
      title: 'Campaign limit reached',
      hint: 'Your ad account has reached the maximum number of active campaigns. Archive old campaigns first.',
    },
  },
  {
    pattern: /targeting.*too.*narrow|audience.*too.*small/i,
    info: {
      title: 'Target audience too small',
      hint: 'The targeting is too restrictive. Broaden the audience (location, age, or interests) and regenerate.',
    },
  },

  // ── Network / server ─────────────────────────────────────────────────────
  {
    pattern: /network.*error|connection.*refused|econnreset|timeout|etimedout/i,
    info: {
      title: 'Network error',
      hint: 'A network error occurred during publishing. Check your connection and try again.',
    },
  },
  {
    pattern: /service.*unavailable|503|502|500/i,
    info: {
      title: 'Platform service unavailable',
      hint: 'The platform API is temporarily unavailable. Wait a few minutes and retry.',
    },
  },
];

/** Fallback for unrecognized errors */
const FALLBACK_ERROR: PublishErrorInfo = {
  title: 'Publishing failed',
  hint: 'An unexpected error occurred. Check the error details below or try again.',
};

/**
 * Classify a raw error string into a structured PublishErrorInfo.
 * Returns the first matching pattern, or a generic fallback.
 */
export function classifyPublishError(rawError: string | undefined | null): PublishErrorInfo {
  if (!rawError) return FALLBACK_ERROR;

  for (const { pattern, info } of ERROR_PATTERNS) {
    if (pattern.test(rawError)) return info;
  }
  return FALLBACK_ERROR;
}
